import React, { useState, useEffect } from "react";
import { Card, Form, Button, Row, Col, Badge } from "react-bootstrap";
import DateRangePicker from "../../../components/common/DateRangePicker";
import { Icon } from "../../../components/common/Icon";
import { FaFilter, FaSearch, FaTimes, FaRobot, FaCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import apiClient from "../../../api/apiClient";

interface FilterValues {
  chatbot_id: string;
  session_id: string;
  start_date: string;
  end_date: string;
}

interface ChatbotOption {
  id: number;
  name: string;
}

interface AIChatbotSessionFilterProps {
  onFilterChange: (filters: FilterValues) => void;
  onReset: () => void;
  loading?: boolean;
  defaultChatbotId?: number;
  hideChatbotSelect?: boolean;
}

const AIChatbotSessionFilter: React.FC<AIChatbotSessionFilterProps> = ({
  onFilterChange,
  onReset,
  loading = false,
  defaultChatbotId,
  hideChatbotSelect = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [chatbots, setChatbots] = useState<ChatbotOption[]>([]);
  const [loadingChatbots, setLoadingChatbots] = useState(false);
  
  const [filters, setFilters] = useState<FilterValues>({
    chatbot_id: "",
    session_id: "",
    start_date: "",
    end_date: "",
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

  // Load chatbots for dropdown
  useEffect(() => {
    const fetchChatbots = async () => {
      setLoadingChatbots(true);
      try {
        const response = await apiClient.get(`/ai-chatbots/list`);
        
        // Handle both paginated and non-paginated responses
        const chatbotData = (response as any).data || response || [];
        setChatbots(chatbotData.map((bot: any) => ({
          id: bot.id,
          name: bot.name
        })));
      } catch (error) {
        console.error("Error fetching chatbots:", error);
      } finally {
        setLoadingChatbots(false);
      }
    };

    fetchChatbots();
  }, [apiUrl]);

  // Initialize default chatbot filter when provided
  useEffect(() => {
    if (defaultChatbotId) {
      setFilters((prev) => ({ ...prev, chatbot_id: String(defaultChatbotId) }));
    }
  }, [defaultChatbotId]);

  // Count active filters
  useEffect(() => {
    const count = Object.values(filters).filter(value => value !== "").length;
    setActiveFiltersCount(count);
  }, [filters]);

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(filters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      chatbot_id: "",
      session_id: "",
      start_date: "",
      end_date: "",
    };
    setFilters(resetFilters);
    onReset();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  // Get today's date for max date validation
  const today = formatDateForInput(new Date());

  return (
    <Card className="filter-card mb-3">
      <Card.Header 
        className="filter-header" 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Icon icon={FaFilter} className="filter-icon" />
            <span className="filter-title">{t("filters")}</span>
            {activeFiltersCount > 0 && (
              <Badge bg="primary" className="filter-count">
                {activeFiltersCount}
              </Badge>
            )}
          </div>
          <Icon 
            icon={isExpanded ? FaTimes : FaSearch} 
            className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
          />
        </div>
      </Card.Header>
      
      {isExpanded && (
        <Card.Body className="filter-body">
          <Row className="g-3">
            {/* Chatbot Selection */}
            {!hideChatbotSelect && (
              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label className="filter-label">
                    <Icon icon={FaRobot} className="me-1" />
                    {t("chatbot")}
                  </Form.Label>
                  <Form.Select
                    value={filters.chatbot_id}
                    onChange={(e) => handleFilterChange("chatbot_id", e.target.value)}
                    disabled={loadingChatbots || loading}
                    onKeyPress={handleKeyPress}
                  >
                    <option value="">{t("all_chatbots")}</option>
                    {chatbots.map((chatbot) => (
                      <option key={chatbot.id} value={chatbot.id}>
                        {chatbot.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}

            {/* Session ID */}
            <Col md={6} lg={3}>
              <Form.Group>
                <Form.Label className="filter-label">
                  {t("session_id")}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("enter_session_id")}
                  value={filters.session_id}
                  onChange={(e) => handleFilterChange("session_id", e.target.value)}
                  disabled={loading}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
            </Col>

            {/* Date Range */}
            <Col md={12} lg={6}>
              <Form.Group>
                <Form.Label className="filter-label">
                  <Icon icon={FaCalendarAlt} className="me-1" />
                  {t("date_range")}
                </Form.Label>
                <div>
                  <DateRangePicker
                    startDate={filters.start_date || undefined}
                    endDate={filters.end_date || undefined}
                    onChange={({ startDate, endDate }) => {
                      handleFilterChange("start_date", startDate);
                      handleFilterChange("end_date", endDate);
                    }}
                    menuClassName="p-3"
                    className="w-100"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row className="mt-3">
            <Col>
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleResetFilters}
                  disabled={loading || activeFiltersCount === 0}
                >
                  <Icon icon={FaTimes} className="me-1" />
                  {t("reset")}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApplyFilters}
                  disabled={loading}
                >
                  <Icon icon={FaSearch} className="me-1" />
                  {t("apply_filters")}
                </Button>
              </div>
            </Col>
          </Row>

          {/* Active Filters Display */}
          {activeFiltersCount > 0 && (
            <Row className="mt-3">
              <Col>
                <div className="active-filters">
                  <small className="text-muted">{t("active_filters")}:</small>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {filters.chatbot_id && (
                      <Badge bg="info" className="filter-badge">
                        {t("chatbot")}: {chatbots.find(c => c.id.toString() === filters.chatbot_id)?.name || filters.chatbot_id}
                      </Badge>
                    )}
                    {filters.session_id && (
                      <Badge bg="info" className="filter-badge">
                        {t("session")}: {filters.session_id.length > 15 ? filters.session_id.substring(0, 15) + '...' : filters.session_id}
                      </Badge>
                    )}
                    {filters.start_date && (
                      <Badge bg="info" className="filter-badge">
                        {t("from")}: {filters.start_date}
                      </Badge>
                    )}
                    {filters.end_date && (
                      <Badge bg="info" className="filter-badge">
                        {t("to")}: {filters.end_date}
                      </Badge>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      )}

      <style>{`
        .filter-card {
          border: 1px solid var(--border-color);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .filter-header {
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color);
          padding: 0.75rem 1rem;
          transition: background-color 0.2s;
        }

        .filter-header:hover {
          background: var(--bg-tertiary);
        }

        .filter-icon {
          color: var(--primary-color);
        }

        .filter-title {
          font-weight: 600;
          color: var(--text-primary);
        }

        .filter-count {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        .expand-icon {
          transition: transform 0.2s;
          color: var(--text-secondary);
        }

        .expand-icon.expanded {
          transform: rotate(180deg);
        }

        .filter-body {
          background: var(--bg-primary);
        }

        .filter-label {
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .form-control, .form-select {
          border: 1px solid var(--border-color);
          border-radius: 0.375rem;
          font-size: 0.875rem;
        }

        .form-control:focus, .form-select:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.25);
        }

        .active-filters {
          padding: 0.75rem;
          background: var(--bg-tertiary);
          border-radius: 0.375rem;
          border: 1px solid var(--border-color);
        }

        .filter-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .filter-header {
            padding: 0.5rem;
          }

          .filter-body {
            padding: 1rem 0.5rem;
          }

          .d-flex.justify-content-end {
            justify-content: center !important;
          }
        }
      `}</style>
    </Card>
  );
};

export default AIChatbotSessionFilter;